<script lang="ts">
  // Free Duel lobby — the duel-only hub (no overworld). Every card is unlocked, the latest Master
  // Rules apply (Extra Monster / Link Zones shown in the arena), and a SEPARATE Free Duel deck is
  // used. The player picks how to play: a quick casual game, a best-of-three match, ranked, or by
  // hosting / browsing an open room.
  //
  // This slice wires Single Duel → Quick (a real Free Duel casual pool). Match, Ranked, host and
  // pool-browsing are the next build (they need the match orchestrator + lobby registry + the
  // separate Free Duel ladder); they're shown here as upcoming so the layout is complete.
  import { onDestroy, onMount } from "svelte";
  import { fade, fly, scale } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import { session, go } from "../stores/session.svelte.ts";
  import { send, listen, logout as netLogout } from "../stores/net.svelte.ts";
  import { watchReplay } from "../stores/duel.svelte.ts";
  import { mm, resetMm } from "../stores/mm.svelte.ts";
  import { chibiFrom } from "../../render/ChibiAvatar.ts";
  import PortraitPreview from "../components/PortraitPreview.svelte";
  import QueueOverlay from "../components/QueueOverlay.svelte";
  import DeckChooser from "../components/DeckChooser.svelte";
  import DataWindow from "../components/ui/DataWindow.svelte";
  import Toast from "../components/ui/Toast.svelte";
  import NpcBustView from "../components/NpcBustView.svelte";
  import { draggableDialog } from "../actions/draggableDialog.ts";
  import { allNpcs, type WorldNpc } from "../world/npcs.ts";
  import { npcs as npcData } from "@ygoevo/yo2-data";
  import type { BestOf, DeckSummary, LobbyInfo, RankInfo, RankMode } from "@ygoevo/world-protocol";

  const name = $derived(session.loginOk?.avatar.name ?? "Duelist");
  const dp = $derived(session.loginOk?.wallet.dp ?? 0);
  const look = $derived(chibiFrom(session.loginOk?.avatar));

  const reduced =
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  const d = (ms: number) => (reduced ? 0 : ms);

  // Tournament main-deck bounds (mirrors the server's deck.ts + the Deck Builder).
  const MAIN_MIN = 40;
  const MAIN_MAX = 60;

  // ── Free Duel decks (separate namespace from the World collection) ──
  // There's no equipped deck: the player picks which one to bring in the DeckChooser shown before each
  // duel. The lobby just tracks whether they have ANY tournament-legal deck (to label the status strip).
  let decks = $state<DeckSummary[]>([]);
  const playableDecks = $derived(decks.filter((x) => x.main.length >= MAIN_MIN && x.main.length <= MAIN_MAX));
  const hasPlayableDeck = $derived(playableDecks.length > 0);

  // Only the `free` deck list belongs to this screen (the World editor uses the same message).
  listen("Decks", (m) => {
    if (m.format !== "free") return;
    decks = m.decks;
  });

  // Pre-duel deck picker: every duel-entry action opens it, and the chosen deck name rides the entry
  // message (queue / host / join). `run` fires once a deck is chosen.
  let chooser = $state<{ subtitle: string; run: (deck: string) => void } | null>(null);
  function chooseDeck(subtitle: string, run: (deck: string) => void) {
    closeBrowse();
    chooser = { subtitle, run };
  }

  // ── Duel a Bot: pick any World NPC (it brings its signature deck) and duel it with a Free Duel deck ──
  const bots = allNpcs();
  const bustFor = (char: string) => npcData.busts.find((b) => b.id === char) ?? null;
  let botOpen = $state(false);
  function openBots() { closeBrowse(); botOpen = true; }
  function pickBot(n: WorldNpc) {
    botOpen = false;
    // Same flow as the overworld NPC duel, but in the Free Duel namespace (unrestricted decks, no DP).
    chooseDeck(`Duel ${n.name}`, (deck) => send({ t: "DuelPractice", npc: n.char, deck, format: "free" }));
  }

  // Matchmaking heartbeat → the floating QueueOverlay (the lobby owns these while World isn't mounted).
  listen("QueueStatus", (m) => {
    if (!m.searching) return resetMm();
    mm.searching = true;
    mm.mode = m.mode;
    mm.format = m.format ?? "free";
    mm.waited = m.waited;
    mm.pool = m.pool;
  });
  listen("MatchFound", () => (mm.found = true));

  // The player's Free Duel ranked standing (separate ladder from World). Requested on mount + refreshed
  // after a ranked game (the server pushes RankInfo with format:"free"). Each ranked mode now climbs
  // its OWN ladder — this badge tracks the free-single one (the pre-split Free Duel ladder).
  let freeRank = $state<RankInfo | null>(null);
  listen("RankInfo", (m) => {
    if (m.ladder ? m.ladder === "free-single" : m.format === "free") freeRank = m.ranking;
  });

  let flash = $state<string | null>(null);
  let flashTimer: ReturnType<typeof setTimeout> | undefined;
  function notify(msg: string) {
    flash = msg;
    clearTimeout(flashTimer);
    flashTimer = setTimeout(() => (flash = null), 3500);
  }
  listen("Error", (m) => notify(m.message));

  // ── Open-challenge lobbies (host an open room / browse + join one) ──
  let browseOpen = $state(false);
  let lobbyList = $state<LobbyInfo[]>([]);
  let hosting = $state<null | { bestOf: BestOf }>(null);
  let pollTimer: ReturnType<typeof setInterval> | undefined;

  listen("Lobbies", (m) => (lobbyList = m.lobbies));
  // LobbyHosted just confirms the open lobby exists; the hosting overlay is already up (optimistic).

  function openBrowse() {
    browseOpen = true;
    send({ t: "LobbyList" });
    clearInterval(pollTimer);
    pollTimer = setInterval(() => browseOpen && send({ t: "LobbyList" }), 2500); // poll while open
  }
  function closeBrowse() {
    browseOpen = false;
    clearInterval(pollTimer);
  }
  function joinLobby(id: string) {
    // Pick a deck, then join with it. A successful join fires DuelStart (App routes us to the duel);
    // an Error keeps us here, and the 2.5s poll refreshes the (now-stale) list.
    chooseDeck("Join Lobby", (deck) => send({ t: "LobbyJoin", lobbyId: id, deck }));
  }
  function hostLobby(bestOf: BestOf) {
    chooseDeck(bestOf === 3 ? "Host · Match (Bo3)" : "Host · Single", (deck) => {
      hosting = { bestOf };
      send({ t: "LobbyHost", format: "free", bestOf, deck });
    });
  }
  function cancelHost() {
    if (!hosting) return;
    hosting = null;
    try { send({ t: "LobbyCancel" }); } catch { /* server drops the lobby on disconnect anyway */ }
  }

  onMount(() => {
    send({ t: "DeckList", format: "free" }); // load the player's Free Duel decks
    send({ t: "RankGet", format: "free" }); // load the player's Free Duel ranked standing
    // Dev: `?lobbymock=browse|host` seeds a deck + sample lobbies and opens an overlay, for design.
    if (import.meta.env.DEV) {
      const lm = new URLSearchParams(location.search).get("lobbymock");
      if (lm) {
        const m: number[] = [];
        for (let i = 0; i < 40; i++) m.push(46986414);
        decks = [{ name: "Dragons", main: m, extra: [], side: [] }];
        lobbyList = [
          { id: "a", hostName: "Yugi Muto", format: "free", bestOf: 1, rank: { rating: 1820, label: "Ra Yellow II", school: "ra" } },
          { id: "b", hostName: "Seto Kaiba", format: "free", bestOf: 3, rank: { rating: 2050, label: "Obelisk Blue II", school: "obelisk" } },
          { id: "c", hostName: "Joey Wheeler", format: "free", bestOf: 1, rank: { rating: 1490, label: "Slifer Red I", school: "slifer" } },
        ];
        freeRank = { rating: 1685, rd: 90, games: 18, wins: 11, losses: 7, peak: 1710, placed: true, school: "ra", division: 2, label: "Ra Yellow II" };
        if (lm === "host") hosting = { bestOf: 3 };
        else if (lm === "browse") browseOpen = true;
      }
    }
  });
  onDestroy(() => clearInterval(pollTimer));

  function leaveQueue() {
    if (!mm.searching) return;
    resetMm();
    try { send({ t: "QueueLeave" }); } catch { /* server drops us on disconnect anyway */ }
  }

  // Enter a matchmaking pool. Every length × rank-mode combination is its own pool
  // (and every ranked one climbs its own ladder).
  function enterQueue(mode: RankMode, bestOf: BestOf) {
    const kind = bestOf === 3 ? "Match · Bo3" : "Single";
    chooseDeck(mode === "ranked" ? `${kind} · Ranked` : kind, (deck) => {
      mm.searching = true;
      mm.mode = mode;
      mm.format = "free";
      mm.waited = 0;
      mm.pool = 0;
      mm.found = false;
      send({ t: "QueueJoin", mode, format: "free", bestOf, deck });
    });
  }

  function editDeck() {
    leaveQueue(); cancelHost(); closeBrowse(); chooser = null;
    go("deck"); // DeckBuilder reads session.mode === "free" → edits the Free Duel deck namespace
  }

  function back() {
    leaveQueue(); cancelHost(); closeBrowse(); botOpen = false;
    go("mode");
  }

  function signOut() {
    leaveQueue(); cancelHost();
    netLogout();
    go("title");
  }

  // The duel options. Quick auto-pairs you with anyone in that pool; Ranked is the same pool
  // rated — each ranked mode climbs its OWN ladder (free singles / free matches).
  interface Action { label: string; run?: () => void; soon?: boolean; variant?: "ghost" | "ranked" }
  interface ModeRow { icon: string; title: string; desc: string; actions: Action[] }
  interface ModeSection { key: string; title: string; sub: string; icon: string; rows: ModeRow[] }
  const sections: ModeSection[] = [
    {
      key: "auto",
      title: "Duels",
      sub: "Casual or ranked — singles and best-of-three",
      icon: "engine",
      rows: [
        {
          icon: "bot",
          title: "Duel a Bot",
          desc: "Practice against any of the World's signature duelists.",
          actions: [{ label: "Choose Opponent", run: openBots }],
        },
        {
          icon: "single",
          title: "Single Duel",
          desc: "One game. Auto-paired with someone in the pool.",
          actions: [
            { label: "Quick", run: () => enterQueue("unranked", 1) },
            { label: "Ranked", run: () => enterQueue("ranked", 1), variant: "ranked" },
          ],
        },
        {
          icon: "match",
          title: "Match Duel · Bo3",
          desc: "Best of three with side-decking between games.",
          actions: [
            { label: "Quick", run: () => enterQueue("unranked", 3) },
            { label: "Ranked", run: () => enterQueue("ranked", 3), variant: "ranked" },
          ],
        },
        {
          icon: "users",
          title: "Browse Lobbies",
          desc: "See who's hosting and choose your opponent.",
          actions: [{ label: "Browse", run: openBrowse }],
        },
        {
          icon: "host",
          title: "Host a Duel",
          desc: "Open your own room and wait for a challenger.",
          actions: [
            { label: "Host Single", run: () => hostLobby(1), variant: "ghost" },
            { label: "Host Match", run: () => hostLobby(3), variant: "ghost" },
          ],
        },
        {
          icon: "replay",
          title: "Watch Replay",
          desc: "Play back a saved replay file with full visibility.",
          actions: [{ label: "Load File", run: () => void watchReplay("freeduel"), variant: "ghost" }],
        },
      ],
    },
  ];

  const bestOfLabel = (b: BestOf) => (b === 3 ? "Match · Bo3" : "Single");
</script>

{#snippet ic(n: string, cls: string)}
  <svg viewBox="0 0 24 24" class={cls} fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    {#if n === "single"}
      <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M12 4v16" />
    {:else if n === "match"}
      <rect x="2.5" y="6" width="13" height="12" rx="1.6" /><rect x="8.5" y="6" width="13" height="12" rx="1.6" />
    {:else if n === "bot"}
      <rect x="4" y="8" width="16" height="12" rx="2" /><path d="M12 8V5" /><circle cx="12" cy="3.5" r="1.3" /><path d="M9 13h.01" /><path d="M15 13h.01" /><path d="M9.5 16.5h5" /><path d="M2 14v2" /><path d="M22 14v2" />
    {:else if n === "ranked"}
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    {:else if n === "host"}
      <path d="M12 5v14" /><path d="M5 12h14" />
    {:else if n === "replay"}
      <circle cx="12" cy="12" r="9" /><path d="m10 8.5 5.5 3.5-5.5 3.5Z" />
    {:else if n === "gavel"}
      <path d="m14 13-7.5 7.5a2.12 2.12 0 1 1-3-3L11 10" /><path d="m16 16 6-6" /><path d="m8 8 6-6" /><path d="m9 7 8 8" /><path d="m21 11-8-8" />
    {:else if n === "engine"}
      <rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /><path d="M9 2v2" /><path d="M15 2v2" /><path d="M9 20v2" /><path d="M15 20v2" /><path d="M2 9h2" /><path d="M2 15h2" /><path d="M20 9h2" /><path d="M20 15h2" />
    {:else if n === "deck"}
      <path d="M12.8 2.2a2 2 0 0 0-1.6 0L2.6 6.1a1 1 0 0 0 0 1.8l8.6 3.9a2 2 0 0 0 1.6 0l8.6-3.9a1 1 0 0 0 0-1.8Z" /><path d="m22 17.7-9.2 4.1a2 2 0 0 1-1.6 0L2 17.7" /><path d="m22 12.7-9.2 4.1a2 2 0 0 1-1.6 0L2 12.7" />
    {:else if n === "edit"}
      <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    {:else if n === "back"}
      <path d="M19 12H5" /><path d="m11 18-6-6 6-6" />
    {:else if n === "arrow"}
      <path d="M5 12h14" /><path d="m13 6 6 6-6 6" />
    {:else if n === "close"}
      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    {:else if n === "refresh"}
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" />
    {:else if n === "users"}
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    {:else if n === "gear"}
      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    {/if}
  </svg>
{/snippet}

<div class="relative grid h-full w-full place-items-start overflow-y-auto px-4 py-5 sm:place-items-center sm:py-8">
  <div class="flex w-full max-w-[44rem] flex-col gap-4">
    <!-- Top bar: back · settings · identity -->
    <div class="flex items-center justify-between" in:fade={{ duration: d(400) }}>
      <div class="flex items-center gap-2">
        <button
          onclick={back}
          class="fx-lift fx-sweep flex items-center gap-1.5 chamfer-btn bg-surface/70 px-3 py-2 font-display text-xs font-semibold uppercase tracking-[0.14em] text-ink-dim hover:bg-surface hover:text-cyan"
          style="--c:7px"
        >
          {@render ic("back", "size-3.5")} Modes
        </button>
        <button
          onclick={() => go("settings")}
          class="fx-lift grid size-9 place-items-center chamfer-btn bg-surface/70 text-ink-dim hover:bg-surface hover:text-cyan hover:glow-cyan"
          style="--c:7px"
          aria-label="Settings"
          title="Settings"
        >
          {@render ic("gear", "size-4")}
        </button>
      </div>
      <div class="flex items-center gap-2.5">
        <div class="leading-tight text-right">
          <div class="font-display text-sm font-bold tracking-wide text-ink">{name}</div>
          <div class="flex items-baseline justify-end gap-1">
            <span class="font-display text-[10px] uppercase tracking-[0.14em] text-gold/80">DP</span>
            <span class="font-display text-xs font-bold tabular-nums text-gold">{dp.toLocaleString()}</span>
          </div>
        </div>
        <div class="grid size-9 place-items-center overflow-hidden chamfer bg-void-2 ring-1 ring-magenta/25" style="--c:7px">
          <PortraitPreview size={36} {look} headshot />
        </div>
      </div>
    </div>

    <!-- Title -->
    <header class="mt-1 flex items-end justify-between" in:fly={{ y: 16, duration: d(520), delay: d(80), easing: expoOut }}>
      <div>
        <h1 class="font-display text-[clamp(1.7rem,5vw,2.6rem)] font-bold uppercase leading-none tracking-[0.04em] text-ink">
          Free <span class="text-magenta text-glow">Duel</span>
        </h1>
        <div class="mt-1.5 flex flex-wrap items-center gap-2.5">
          <p class="text-sm text-ink-dim">Every card. Latest rules. Straight to the duel.</p>
          {#if freeRank}
            <span class="flex items-center gap-1.5 chamfer-btn bg-void-2/70 px-2.5 py-1 ring-1 ring-magenta/25" style="--c:5px" title="Your Free Duel ranked standing (separate from World)">
              <span class="size-1.5 rounded-full bg-magenta" style="filter:drop-shadow(0 0 4px currentColor)"></span>
              <span class="font-display text-[0.66rem] font-bold uppercase tracking-[0.1em] text-ink">{freeRank.label}</span>
              {#if freeRank.placed}<span class="font-display text-[0.66rem] font-bold tabular-nums text-magenta">{freeRank.rating}</span>{/if}
            </span>
          {/if}
        </div>
      </div>
      <button
        onclick={signOut}
        class="hidden font-display text-[0.66rem] uppercase tracking-[0.18em] text-ink-dim/60 transition-colors hover:text-magenta sm:block"
      >
        Sign out
      </button>
    </header>

    <!-- Deck status strip: you pick which deck to bring per-duel, so this just tracks whether you HAVE
         a tournament-legal one (and lets you jump to the editor). -->
    <div in:fly={{ y: 18, duration: d(540), delay: d(150), easing: expoOut }}>
      <div class="chamfer {hasPlayableDeck ? 'bg-lime/40' : 'bg-magenta/50'} p-px" style="--c:13px">
        <div class="chamfer grid-tex flex items-center gap-3.5 bg-surface/92 px-4 py-3.5" style="--c:12px">
          <span class="grid size-10 shrink-0 place-items-center chamfer-btn {hasPlayableDeck ? 'text-lime' : 'text-magenta'}" style="--c:7px; background:color-mix(in oklab, currentColor 13%, transparent)">
            {@render ic("deck", "size-5")}
          </span>
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <span class="font-display text-[0.62rem] font-semibold uppercase tracking-[0.2em] {hasPlayableDeck ? 'text-lime' : 'text-magenta'}">Free Duel Decks</span>
              <span class="size-1.5 rounded-full {hasPlayableDeck ? 'bg-lime' : 'bg-magenta'} animate-pulse-soft" style="filter:drop-shadow(0 0 4px currentColor)"></span>
            </div>
            <div class="truncate font-display text-sm font-bold text-ink">
              {#if hasPlayableDeck}
                {playableDecks.length} ready <span class="font-normal text-ink-dim">· choose one when you duel</span>
              {:else if decks.length}
                {decks.length} deck{decks.length === 1 ? "" : "s"} <span class="font-normal text-ink-dim">· none has {MAIN_MIN}-{MAIN_MAX} cards yet</span>
              {:else}
                No deck yet. Build one to duel
              {/if}
            </div>
          </div>
          <button
            onclick={editDeck}
            class="fx-lift fx-sweep flex shrink-0 items-center gap-1.5 chamfer-btn bg-surface-2 px-4 py-2 font-display text-xs font-semibold uppercase tracking-[0.12em] text-ink hover:text-cyan hover:glow-cyan"
            style="--c:7px"
          >
            {@render ic("edit", "size-3.5")} {decks.length ? "Edit" : "Build Deck"}
          </button>
        </div>
      </div>
    </div>

    <!-- Duel options, one panel per section with a header band. -->
    {#each sections as s, si (s.key)}
      <div class="chamfer bg-cyan-dim/40 p-px" style="--c:16px" in:fly={{ y: 22, duration: d(580), delay: d(220 + si * 110), easing: expoOut }}>
        <div class="chamfer grid-tex relative bg-surface/92" style="--c:15px">
          <div class="pointer-events-none absolute inset-x-6 top-0 h-px" style="background:linear-gradient(90deg,transparent,color-mix(in oklab,var(--color-cyan) 60%,transparent),transparent)"></div>
          <!-- section header -->
          <div class="flex items-center gap-3 border-b border-cyan-dim/30 bg-cyan/[0.03] px-4 py-2.5 sm:px-5">
            <span class="grid size-8 shrink-0 place-items-center chamfer-btn bg-cyan/12 text-cyan" style="--c:6px">
              {@render ic(s.icon, "size-4")}
            </span>
            <div class="flex min-w-0 flex-wrap items-baseline gap-x-2.5">
              <span class="font-display text-[0.78rem] font-bold uppercase tracking-[0.24em] text-cyan" style="text-shadow:0 0 10px var(--color-cyan)">{s.title}</span>
              <span class="text-[0.68rem] italic text-ink-dim">{s.sub}</span>
            </div>
          </div>
          <div class="divide-y divide-cyan-dim/15">
            {#each s.rows as r (r.title)}
              <div class="group flex items-center gap-3.5 px-4 py-3.5 transition-colors duration-200 hover:bg-cyan/[0.045] sm:px-5">
                <span class="grid size-10 shrink-0 place-items-center chamfer-btn bg-cyan/10 text-cyan group-hover:bg-cyan/18 transition-[transform,background-color] duration-200 group-hover:scale-110" style="--c:7px">
                  {@render ic(r.icon, "size-5")}
                </span>
                <div class="min-w-0 flex-1">
                  <div class="font-display text-[0.95rem] font-bold tracking-wide text-ink transition-colors duration-200 group-hover:text-glow">{r.title}</div>
                  <div class="text-xs leading-snug text-ink-dim">{r.desc}</div>
                </div>
                <div class="flex shrink-0 flex-col items-end gap-1.5 sm:flex-row sm:items-center sm:gap-2">
                  {#each r.actions as a (a.label)}
                    {#if a.soon}
                      <span class="chamfer-btn cursor-default bg-void-2/50 px-3 py-1.5 font-display text-[0.66rem] font-semibold uppercase tracking-[0.12em] text-ink-dim/45 ring-1 ring-cyan/10" style="--c:6px" title="Coming in the next build">
                        {a.label} · Soon
                      </span>
                    {:else}
                      <button
                        onclick={a.run}
                        class="fx-lift fx-sweep chamfer-btn px-4 py-1.5 font-display text-[0.7rem] font-bold uppercase tracking-[0.12em] {a.variant === 'ranked'
                          ? 'bg-gold/12 text-gold ring-1 ring-gold/45 hover:bg-gold/20 hover:glow-gold'
                          : a.variant === 'ghost'
                            ? 'bg-surface-2 text-cyan ring-1 ring-cyan/30 hover:text-ink hover:glow-cyan'
                            : 'bg-cyan text-void hover:brightness-110 hover:glow-cyan'}"
                        style="--c:6px"
                        title={a.variant === "ranked" ? "Rated — climbs this mode's own ladder" : undefined}
                      >
                        {a.label}
                      </button>
                    {/if}
                  {/each}
                </div>
              </div>
            {/each}
          </div>
        </div>
      </div>
    {/each}

    <p class="px-1 text-center text-xs leading-relaxed text-ink-dim/80" in:fade={{ duration: d(400), delay: d(320) }}>
      Free Duel decks are separate from your World collection, but the foils you earned in the World still shine here.
    </p>
  </div>
</div>

<!-- Pre-duel deck picker (every duel-entry action chooses a Free Duel deck first) -->
{#if chooser}
  <DeckChooser
    format="free"
    accent="magenta"
    subtitle={chooser.subtitle}
    onpick={(deck) => { const run = chooser!.run; chooser = null; run(deck); }}
    oncancel={() => (chooser = null)}
    onbuild={editDeck}
  />
{/if}

<!-- Matchmaking search overlay (shared store) -->
<QueueOverlay />

<!-- ── Hosting overlay: an open lobby, waiting for a challenger ── -->
{#if hosting}
  <div class="fixed inset-0 z-50 grid place-items-center" transition:fade={{ duration: 160 }}>
    <button class="modal-scrim absolute inset-0 bg-void/50" onclick={cancelHost} aria-label="Cancel hosting"></button>
    <div use:draggableDialog role="dialog" aria-modal="true" aria-label="Open lobby" class="relative w-[min(380px,92vw)]" transition:scale={{ start: 0.96, opacity: 0, duration: 220, easing: expoOut }}>
      <DataWindow c={16} accent="magenta">
        <div class="flex flex-col items-center gap-4 px-7 py-7">
          <div class="dialog-drag-handle -mb-3 -mt-4 h-5 w-full shrink-0" data-dialog-drag-handle aria-hidden="true"></div>
          <span class="relative grid size-12 place-items-center">
            <span class="absolute inset-0 rounded-full border-2 border-magenta/25"></span>
            <span class="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-magenta" style="animation-duration:0.9s"></span>
            <span class="size-2.5 rounded-full bg-magenta animate-pulse-soft" style="filter:drop-shadow(0 0 6px currentColor)"></span>
          </span>
          <div class="text-center">
            <div class="font-display text-sm font-bold uppercase tracking-[0.18em] text-magenta">Lobby Open</div>
            <div class="mt-1 font-display text-lg font-bold text-ink">{bestOfLabel(hosting.bestOf)}</div>
            <div class="mt-1 text-xs text-ink-dim">Waiting for a challenger to join<span class="ellipsis"></span></div>
          </div>
          <button
            onclick={cancelHost}
            class="chamfer-btn bg-void-2/60 px-6 py-2 font-display text-xs font-semibold uppercase tracking-widest text-ink-dim transition-colors hover:bg-magenta/10 hover:text-magenta"
            style="--c:7px"
          >
            Cancel
          </button>
        </div>
      </DataWindow>
    </div>
  </div>
{/if}

<!-- ── Lobby browser: open challenges others have hosted ── -->
{#if browseOpen}
  <div class="mobile-dialog-host fixed inset-0 z-50 grid place-items-center px-4" transition:fade={{ duration: 160 }}>
    <button class="modal-scrim absolute inset-0 bg-void/50" onclick={closeBrowse} aria-label="Close lobby browser"></button>
    <div use:draggableDialog role="dialog" aria-modal="true" aria-label="Open lobbies" class="mobile-full-dialog relative w-[min(520px,94vw)] max-h-[82dvh]" transition:scale={{ start: 0.96, opacity: 0, duration: 220, easing: expoOut }}>
      <DataWindow c={16} fill class="mobile-dialog-panel">
        <div class="dialog-drag-handle flex items-center justify-between border-b border-cyan-dim/40 bg-surface-2/70 px-5 py-3" data-dialog-drag-handle>
          <span class="flex items-center gap-2">
            {@render ic("users", "size-4 text-cyan")}
            <span class="font-display text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-cyan">Open Lobbies</span>
            <span class="font-display text-xs tabular-nums text-ink-dim">· {lobbyList.length}</span>
          </span>
          <span class="flex items-center gap-1">
            <button onclick={() => send({ t: "LobbyList" })} class="grid size-7 place-items-center text-ink-dim transition-colors hover:text-cyan" title="Refresh" aria-label="Refresh">
              {@render ic("refresh", "size-4")}
            </button>
            <button onclick={closeBrowse} class="grid size-7 place-items-center text-ink-dim transition-colors hover:text-cyan" aria-label="Close">
              {@render ic("close", "size-4")}
            </button>
          </span>
        </div>

        <div class="mobile-dialog-scroll max-h-[60dvh] overflow-y-auto scroll-cyber p-2.5">
          {#if lobbyList.length === 0}
            <div class="flex flex-col items-center gap-2 px-6 py-12 text-center">
              <span class="text-ink-dim/40">{@render ic("host", "size-7")}</span>
              <div class="font-display text-sm font-semibold text-ink-dim">No open lobbies right now</div>
              <div class="max-w-[26ch] text-xs text-ink-dim/60">Host one yourself, or use Quick Match to be paired automatically.</div>
            </div>
          {:else}
            <div class="space-y-1.5">
              {#each lobbyList as l (l.id)}
                <div class="flex items-center gap-3 chamfer bg-void-2/45 px-3.5 py-2.5" style="--c:9px">
                  <span class="grid size-9 shrink-0 place-items-center chamfer-btn {l.bestOf === 3 ? 'bg-magenta/12 text-magenta' : 'bg-cyan/12 text-cyan'}" style="--c:6px">
                    {@render ic(l.bestOf === 3 ? "match" : "single", "size-5")}
                  </span>
                  <div class="min-w-0 flex-1">
                    <div class="truncate font-display text-sm font-bold text-ink">{l.hostName}</div>
                    <div class="flex items-center gap-2 text-[0.7rem] text-ink-dim">
                      <span class="font-semibold uppercase tracking-[0.1em] {l.bestOf === 3 ? 'text-magenta' : 'text-cyan'}">{bestOfLabel(l.bestOf)}</span>
                      <span class="text-ink-dim/40">·</span>
                      <span class="tracking-wide">{l.rank.label}</span>
                    </div>
                  </div>
                  <button
                    onclick={() => joinLobby(l.id)}
                    class="fx-lift fx-sweep chamfer-btn shrink-0 bg-cyan px-4 py-1.5 font-display text-[0.7rem] font-bold uppercase tracking-[0.12em] text-void hover:brightness-110 hover:glow-cyan"
                    style="--c:6px"
                  >
                    Join
                  </button>
                </div>
              {/each}
            </div>
          {/if}
        </div>
      </DataWindow>
    </div>
  </div>
{/if}

<!-- ── Bot opponent picker: every World NPC, each bringing its signature deck ── -->
{#if botOpen}
  <div class="mobile-dialog-host fixed inset-0 z-50 grid place-items-center px-4" transition:fade={{ duration: 160 }}>
    <button class="modal-scrim absolute inset-0 bg-void/50" onclick={() => (botOpen = false)} aria-label="Close opponent picker"></button>
    <div use:draggableDialog role="dialog" aria-modal="true" aria-label="Choose an opponent" class="mobile-full-dialog relative w-[min(640px,95vw)] max-h-[86dvh]" transition:scale={{ start: 0.96, opacity: 0, duration: 220, easing: expoOut }}>
      <DataWindow c={16} accent="magenta" fill class="mobile-dialog-panel">
        <div class="dialog-drag-handle flex items-center justify-between border-b border-magenta/30 bg-surface-2/70 px-5 py-3" data-dialog-drag-handle>
          <span class="flex items-center gap-2">
            {@render ic("bot", "size-4 text-magenta")}
            <span class="font-display text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-magenta">Choose an Opponent</span>
            <span class="font-display text-xs tabular-nums text-ink-dim">· {bots.length}</span>
          </span>
          <button onclick={() => (botOpen = false)} class="grid size-7 place-items-center text-ink-dim transition-colors hover:text-magenta" aria-label="Close">
            {@render ic("close", "size-4")}
          </button>
        </div>
        <div class="mobile-dialog-scroll max-h-[68dvh] overflow-y-auto scroll-cyber p-3">
          <div class="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            {#each bots as n (n.char)}
              {@const bust = bustFor(n.char)}
              <button
                onclick={() => pickBot(n)}
                class="group fx-lift flex flex-col items-center gap-2 chamfer bg-void-2/45 p-3 ring-1 ring-magenta/10 hover:bg-magenta/10 hover:ring-magenta/40 hover:glow-magenta"
                style="--c:10px"
              >
                <div class="grid size-[88px] place-items-center overflow-hidden chamfer bg-void ring-1 ring-magenta/20" style="--c:8px">
                  {#if bust}
                    <span class="block transition-transform duration-300 ease-[var(--ease-out-quint)] group-hover:scale-110">
                      <NpcBustView {bust} height={88} windowed />
                    </span>
                  {:else}
                    <span class="font-display text-2xl font-bold text-magenta/60">{n.name[0]}</span>
                  {/if}
                </div>
                <div class="w-full text-center leading-tight">
                  <div class="truncate font-display text-[0.82rem] font-bold text-ink group-hover:text-magenta">{n.name}</div>
                  <div class="truncate text-[0.62rem] uppercase tracking-[0.08em] text-ink-dim/70">{n.title}</div>
                </div>
              </button>
            {/each}
          </div>
        </div>
      </DataWindow>
    </div>
  </div>
{/if}

<!-- Transient notice (e.g. "build a deck first", server errors) -->
{#if flash}
  <div class="pointer-events-none fixed inset-x-0 top-6 z-[55] flex justify-center" transition:fade>
    <Toast kind="error">{flash}</Toast>
  </div>
{/if}

<style>
  .ellipsis::after {
    content: "";
    animation: dots 1.4s steps(4, end) infinite;
  }
  @keyframes dots {
    0% { content: ""; }
    25% { content: "."; }
    50% { content: ".."; }
    75% { content: "..."; }
  }
  @media (prefers-reduced-motion: reduce) {
    .ellipsis::after { animation: none; }
  }
</style>
